import { Bell, CheckCheck, CircleAlert, CircleCheck, Info, TriangleAlert, X } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { api, type AppNotification } from '../lib/api'

interface Props {
  open: boolean
  onClose: () => void
}

export function NotificationCenter({ open, onClose }: Props) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const notifications = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api<AppNotification[]>('/api/v1/notifications?limit=100'),
    refetchInterval: 60_000,
  })
  const markAllRead = useMutation({
    mutationFn: () => api<void>('/api/v1/notifications/read', { method: 'PUT' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  })
  const unread = notifications.data?.filter((item) => !item.read).length ?? 0

  if (!open) return null
  return (
    <div className="drawer-backdrop" onMouseDown={onClose}>
      <aside className="notification-drawer" onMouseDown={(event) => event.stopPropagation()}>
        <header className="drawer-header">
          <div>
            <span className="eyebrow"><Bell size={14} /> {unread}</span>
            <h2>{t('notifications.title')}</h2>
          </div>
          <button className="icon-button" onClick={onClose} aria-label={t('common.close')}><X /></button>
        </header>
        <button className="quiet-button full-width" onClick={() => markAllRead.mutate()}>
          <CheckCheck size={16} /> {t('notifications.markAllRead')}
        </button>
        <div className="notification-list">
          {notifications.data?.map((notification) => (
            <article key={notification.id} className={`notification-item ${notification.read ? '' : 'unread'}`}>
              <NotificationIcon severity={notification.severity} />
              <div>
                <strong>{t(notification.titleKey, notification.arguments)}</strong>
                <p>{t(notification.messageKey, notification.arguments)}</p>
                <time>{new Date(notification.createdAt).toLocaleString('de-DE')}</time>
              </div>
            </article>
          ))}
          {!notifications.isLoading && notifications.data?.length === 0 && (
            <div className="empty-state compact"><Bell /><p>{t('notifications.empty')}</p></div>
          )}
        </div>
      </aside>
    </div>
  )
}

function NotificationIcon({ severity }: { severity: AppNotification['severity'] }) {
  if (severity === 'SUCCESS') return <CircleCheck className="severity-success" />
  if (severity === 'WARNING') return <TriangleAlert className="severity-warning" />
  if (severity === 'ERROR') return <CircleAlert className="severity-error" />
  return <Info className="severity-info" />
}

